import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ClickAwayListener, MenuList } from '@mui/material';
import { MenuProps } from './types';
import { bodyZoomScale } from './common';
import { MenuSurface } from './MenuSurface';

export const Menu = ({
  open,
  anchorEl,
  onClose,
  children,
  position = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    transformOrigin: { vertical: 'bottom', horizontal: 'left' }
  }
}: MenuProps) => {
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!anchorEl || !open || !menuRef.current) return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const zoomScale = bodyZoomScale();

    const isSubMenu = anchorEl.closest('.MuiMenuItem-root');

    if (isSubMenu) {
      setMenuPosition({
        top: anchorRect.top / zoomScale,
        left: anchorRect.right / zoomScale
      });
      return;
    }

    let top = anchorRect.top;
    let left = anchorRect.left;

    if (position.anchorOrigin.vertical === 'bottom') {
      top = anchorRect.bottom;
    }
    if (position.transformOrigin.vertical === 'bottom') {
      top -= menuRect.height;
    }

    if (position.anchorOrigin.horizontal === 'right') {
      left = anchorRect.right;
    }
    if (position.transformOrigin.horizontal === 'right') {
      left -= menuRect.width;
    }

    setMenuPosition({
      top: top / zoomScale,
      left: left / zoomScale
    });
  }, [anchorEl, open, position]);

  useEffect(() => {
    const timeoutId = setTimeout(updatePosition, 0);

    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  if (!open) return null;

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        top: menuPosition.top,
        left: menuPosition.left,
        zIndex: 1300
      }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <MenuSurface>
          <MenuList autoFocusItem={open}>{children}</MenuList>
        </MenuSurface>
      </ClickAwayListener>
    </div>
  );
};
